import _ from 'lodash';
import { $apply, $each, $end, $none, $set, update } from 'qim';

import ActionTypes from 'app/explore/ActionTypes';
import { DEFAULT_NUM_SHARED_ZAPS } from 'app/explore/constants';
import { createReducer } from 'app/common/utils/ReduxUtils';
import { POPULARITY, serviceTypes } from 'app/service/constants';

const serviceTypesByDisplayText = _.invert(serviceTypes);

const initialState = {
  blogPosts: {
    updateTimestamp: undefined,
    isLoading: false,
    postData: {},
  },
  currentlyLoadingAppCards: [],
  recommendedSharedZaps: {
    isLoading: false,
    perfectMatchesShown: DEFAULT_NUM_SHARED_ZAPS,
    shownByService: {},
    updateTimestamp: undefined,
    zapData: {},
    zapIds: {},
  },
  searchQuery: '',
  serviceSortingRule: serviceTypes[POPULARITY],
  servicesWithOpenCardMenus: [],
  sortingRulesText: 'Sort By',
};

const handlers = {
  [ActionTypes.CLOSE_ALL_CARD_EXTRA_MENUS](state) {
    return update(['servicesWithOpenCardMenus', $set([])], state);
  },

  [ActionTypes.LOAD_BLOG_POSTS](state, { timestampId }) {
    return update(
      [
        'blogPosts',
        ['isLoading', $set(true)],
        ['updateTimestamp', $set(timestampId)],
      ],
      state
    );
  },

  [ActionTypes.LOAD_BLOG_POSTS_DONE](state, { result, timestampId }) {
    if (timestampId === state.blogPosts.updateTimestamp) {
      state = update(
        ['blogPosts', ['isLoading', $set(false)], ['postData', $set(result)]],
        state
      );
    }
    return state;
  },

  [ActionTypes.LOAD_POPULAR_ZAPS_DONE](state, { result }) {
    const zapData = _.extend({}, state.recommendedSharedZaps.zapData, result);
    return update(['recommendedSharedZaps', 'zapData', $set(zapData)], state);
  },

  [ActionTypes.LOAD_POPULAR_ZAP_IDS](state, { timestampId }) {
    return update(
      [
        'recommendedSharedZaps',
        ['isLoading', $set(true)],
        ['updateTimestamp', $set(timestampId)],
      ],
      state
    );
  },

  [ActionTypes.LOAD_POPULAR_ZAP_IDS_DONE](state, { result, timestampId }) {
    if (timestampId === state.recommendedSharedZaps.updateTimestamp) {
      const shownByService = _.extend(
        {},
        state.recommendedSharedZaps.shownByService
      );

      for (const key in result.by_service) {
        if (!shownByService[key]) {
          shownByService[key] = DEFAULT_NUM_SHARED_ZAPS;
        }
      }

      state = update(
        [
          'recommendedSharedZaps',
          ['isLoading', $set(false)],
          ['shownByService', $set(shownByService)],
          ['zapIds', $set(result)],
        ],
        state
      );
    }
    return state;
  },

  [ActionTypes.RESET_NUMBER_ZAPS_TO_SHOW](state) {
    const newState = update(
      [
        'recommendedSharedZaps',
        ['perfectMatchesShown', $set(DEFAULT_NUM_SHARED_ZAPS)],
        ['shownByService', $set({})],
      ],
      state
    );
    return newState;
  },

  [ActionTypes.SHOW_MORE_SHARED_ZAPS](state, { number, sectionName }) {
    if (sectionName === 'perfect-matches') {
      return update(
        [
          'recommendedSharedZaps',
          'perfectMatchesShown',
          $apply(_.partial(_.add, number)),
        ],
        state
      );
    } else {
      const previouslyShown =
        state.recommendedSharedZaps.shownByService[sectionName] ||
        DEFAULT_NUM_SHARED_ZAPS;
      return update(
        [
          'recommendedSharedZaps',
          'shownByService',
          sectionName,
          $set(previouslyShown + number),
        ],
        state
      );
    }
  },

  [ActionTypes.TOGGLE_LOADING_CARD](state, { serviceKey, isLoading }) {
    if (isLoading) {
      state = update(
        ['currentlyLoadingAppCards', $end, $set([serviceKey])],
        state
      );
    } else {
      state = update(
        ['currentlyLoadingAppCards', $each, val => val === serviceKey, $none],
        state
      );
    }
    return state;
  },

  [ActionTypes.TOGGLE_SERVICE_CARD_EXTRAS_MENU](state, { serviceKey }) {
    const index = state.servicesWithOpenCardMenus.indexOf(serviceKey);
    if (index === -1) {
      return update(
        ['servicesWithOpenCardMenus', $end, $set([serviceKey])],
        state
      );
    } else {
      return update(['servicesWithOpenCardMenus', index, $none], state);
    }
  },

  [ActionTypes.UPDATE_SERVICE_SEARCH_QUERY](state, { query }) {
    return update(['searchQuery', $set(query)], state);
  },

  [ActionTypes.UPDATE_SERVICE_SORTING_RULE](state, { sortingRule }) {
    const sortingRulesText = serviceTypesByDisplayText[sortingRule]
      ? 'Sort By'
      : 'Filter By';
    return update(
      [
        ['serviceSortingRule', $set(sortingRule)],
        ['sortingRulesText', $set(sortingRulesText)],
      ],
      state
    );
  },
};

export default createReducer(handlers, initialState);
